import type { PractitionerRole, Profession } from '@common/enums';
import type { IEmployment as EmploymentWithPractitioner } from '@common/interfaces';
import type { EmploymentGetOneApiResult } from '@common/interfaces/api/employmentsApi';
import type { ChangeToEmploymentApiResult } from '@common/interfaces/api/employmentsApiService';
import type { IEmploymentUpdateValidated } from '@common/validation/employment';
import type { IEmployment, IOrganization, IPractitioner } from '@frontend/components/interfaces';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { fetchJson } from '@frontend/store/fetch';

const baseUrl = '/api/employments';

export async function getPractitionersInOrganization(
    organizationId: string,
    professions: Profession[] | undefined,
    signal: AbortSignal
): Promise<EmploymentWithPractitioner[]> {
    const params = new URLSearchParams({
        isActive: 'true',
        ...(professions?.length && { professions: professions.join(',') }),
    });

    const url = `${baseUrl}/${organizationId}/practitioners?${params.toString()}`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json.items;
}

export interface EmploymentWithPractitionerAndOrganizationApiResult extends IEmployment {
    canLoginToCs: boolean;
    employmentPractitionerRoles: PractitionerRole[];
    hasActiveRights: boolean;
    organization: IOrganization;
    practitioner: IPractitioner & Record<'profilePictureUrl', string | undefined>;
    profession: Profession;
}

export async function fetchEmploymentsByOrganizationId(
    organizationId: string,
    includeHasActiveRights: boolean,
    signal: AbortSignal
) {
    const url = `/api/employments/${organizationId}/practitioners?includeProfileThumbnail=true&includeRoles=true${includeHasActiveRights ? '&includeHasActiveRights=true' : ''}`;

    const result = await fetchJson<{ items: EmploymentWithPractitionerAndOrganizationApiResult[] }>({
        method: 'GET',
        signal,
        url,
    });

    return result.json ? result.json.items : [];
}

export async function getEmployment(employmentId: string, signal: AbortSignal) {
    const url = `/api/employments/${employmentId}`;
    const response = await fetchJson<EmploymentGetOneApiResult>({ method: 'GET', signal, url });

    return nonNullable(response.json);
}

export async function changeToEmployment(employmentId: string, signal: AbortSignal) {
    const url = '/api/user/employment';
    const body = { employmentId };
    const response = await fetchJson<ChangeToEmploymentApiResult>({ body, method: 'POST', signal, url });

    return nonNullable(response.json);
}

export async function updateEmployment(employmentId: string, body: IEmploymentUpdateValidated, signal: AbortSignal) {
    const url = `/api/employments/${employmentId}`;
    await fetchJson({ body, method: 'POST', signal, url });
}

export async function getEmploymentByPractitionerIdAndOrganizationId(
    practitionerId: string,
    organizationId: string,
    signal: AbortSignal
) {
    const url = `/api/employments?practitionerId=${practitionerId}&organizationId=${organizationId}`;
    const response = await fetchJson<{ employments: IEmployment[] }>({ method: 'GET', signal, url });

    return response.json?.employments.at(0);
}
